import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText, Download, RefreshCw, Copy, Wand2, Check
} from "lucide-react";
import { API, BACKEND_URL } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

/* ---------- Verified Package Preview Tree ---------- */
export function FileInspectModal({ jobId, file, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) { setLoading(true); setData(null); setErr(null); }
    });
    axios.get(`${API}/ingest/jobs/${jobId}/file-inspect`, { params: { path: file.path } })
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e?.response?.data?.detail || e?.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [jobId, file]);

  if (!file) return null;
  const directUrl = `${BACKEND_URL}/api/ingest/jobs/${jobId}/file?path=${encodeURIComponent(file.path)}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="file-inspect-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500">File inspection</div>
            <div className="font-mono text-sm font-semibold truncate" title={file.name}>{file.name}</div>
            <div className="font-mono text-[11px] text-slate-500 truncate" title={file.path}>{file.path}</div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={directUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] uppercase tracking-wider font-semibold px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
            >
              Open / download
            </a>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X className="w-4 h-4"/></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Preview pane */}
          <div className="border border-slate-200 rounded bg-slate-50 flex items-center justify-center min-h-[280px]">
            {file.kind === "image" && (
              <img src={directUrl} alt={file.name} className="max-w-full max-h-[60vh] object-contain"/>
            )}
            {file.kind === "pdf" && (
              <iframe title={file.name} src={directUrl} className="w-full h-[60vh]" />
            )}
            {file.kind === "workbook" && (
              <div className="p-4 text-center">
                <FileCheck2 className="w-12 h-12 mx-auto text-slate-400 mb-2"/>
                <div className="text-sm text-slate-700 mb-2">Spreadsheets can&apos;t render inline.</div>
                <a href={directUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-block text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 bg-slate-900 text-white rounded hover:bg-slate-700">
                  Download .xlsx
                </a>
              </div>
            )}
            {file.kind === "other" && (
              <div className="p-4 text-center text-sm text-slate-500">
                No inline preview for .{file.path?.split(".").pop() || "file"}
              </div>
            )}
          </div>
          {/* Quality panel */}
          <div>
            <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500 mb-2">Quality checks</div>
            {loading && <div className="text-[12px] text-slate-500 italic">Inspecting…</div>}
            {err && <div className="text-[12px] text-red-600">Inspect error: {err}</div>}
            {data && (
              <div className="space-y-3 text-[12px]">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">Size</div>
                    <div className="font-mono">{fmtBytes(data.size_bytes)}</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">Kind</div>
                    <div className="font-mono">{data.kind}{data.subtype ? ` · ${data.subtype}` : ""}</div>
                  </div>
                </div>
                {data.checks?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Checks</div>
                    <ul className="space-y-0.5">
                      {data.checks.map((c, i) => (
                        <li key={i} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1">
                          <span className="text-slate-700">{c.name}</span>
                          <span className={`font-mono text-[11px] ${c.ok ? "text-emerald-700" : "text-red-600"}`}>
                            {c.ok ? "✓" : "✗"} {String(c.value ?? "—")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.quality_flags?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">Quality flags</div>
                    <ul className="list-disc pl-4 text-amber-900 space-y-0.5">
                      {data.quality_flags.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                )}
                {data.kind === "image" && data.exif && Object.keys(data.exif).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">EXIF</div>
                    <pre className="bg-slate-50 border border-slate-200 rounded p-2 font-mono text-[10.5px] overflow-x-auto">{JSON.stringify(data.exif, null, 2)}</pre>
                  </div>
                )}
                {data.kind === "pdf" && data.first_page_snippet && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">First-page text</div>
                    <pre className="bg-slate-50 border border-slate-200 rounded p-2 font-mono text-[10.5px] whitespace-pre-wrap max-h-40 overflow-y-auto">{data.first_page_snippet}</pre>
                  </div>
                )}
                {data.kind === "workbook" && data.sheets && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Sheets ({data.sheets.length})</div>
                    <ul className="font-mono text-[10.5px] space-y-0.5">
                      {data.sheets.map((s, i) => (
                        <li key={i} className="flex items-center justify-between border-b border-slate-100 py-0.5">
                          <span className="truncate" title={s.name}>{s.name}</span>
                          <span className="text-slate-500">{s.rows} × {s.cols}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.error && <div className="text-red-600">{data.error}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SourceTreeSortableHeader({ id, sortBy, sortDir, onSort, children }) {
  return (
    <button
      type="button"
      onClick={() => onSort(id)}
      className="inline-flex items-center gap-1 hover:text-slate-900"
      data-testid={`source-tree-sort-${id}`}
    >
      {children}
      {sortBy === id && <span className="text-[9px]">{sortDir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}

export function SourcePackageTree({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("path");
  const [sortDir, setSortDir] = useState("asc");
  const [groupByFolder, setGroupByFolder] = useState(true);
  const [openFolders, setOpenFolders] = useState({});
  const [showLimit, setShowLimit] = useState(200);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) { setLoading(true); setErr(null); }
    });
    axios.get(`${API}/ingest/jobs/${jobId}/source-tree`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || e?.message || "Failed to load file tree"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]);

  const CLASS_GROUPS = useMemo(() => ({
    all: () => true,
    forms: (k) => ["form_306", "ew_assessment_spreadsheet", "irw_assessment_spreadsheet", "epar_assessment_form", "smartygrants_application_pdf"].includes(k),
    cet: (k) => k === "cost_estimation",
    ledger: (k) => k === "financial_summary",
    slip: (k) => k === "slip_construction_estimate",
    photos: (k) => k === "evidence_photo" || k === "image",
    evidence_zips: (k) => k === "evidence_archive",
    pdfs: (k) => k === "pdf_attachment" || k === "site_risk_assessment" || k === "certificate_of_currency" || k === "smartygrants_application_pdf",
    spreadsheets: (k) => k === "spreadsheet",
    other: (k) => k === "other" || k === "email" || k === "screening" || k === "rate_evidence",
  }), []);

  const filteredNodes = useMemo(() => {
    if (!data) return [];
    const pred = CLASS_GROUPS[filter] || CLASS_GROUPS.all;
    const q = search.trim().toLowerCase();
    let nodes = data.nodes.filter(n => pred(n.classification));
    if (q) nodes = nodes.filter(n => n.path.toLowerCase().includes(q) || n.name.toLowerCase().includes(q));
    const dir = sortDir === "asc" ? 1 : -1;
    nodes = [...nodes].sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case "size": av = a.size; bv = b.size; break;
        case "mtime": av = a.mtime || 0; bv = b.mtime || 0; break;
        case "classification": av = a.classification; bv = b.classification; break;
        case "ext": av = a.ext; bv = b.ext; break;
        default: av = a.path.toLowerCase(); bv = b.path.toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return nodes;
  }, [data, filter, search, sortBy, sortDir, CLASS_GROUPS]);

  const grouped = useMemo(() => {
    if (!groupByFolder) return { "": filteredNodes };
    const m = {};
    for (const n of filteredNodes) {
      const folder = n.path.includes("/") ? n.path.slice(0, n.path.lastIndexOf("/")) : "(root)";
      (m[folder] = m[folder] || []).push(n);
    }
    return m;
  }, [filteredNodes, groupByFolder]);

  const refresh = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/ingest/jobs/${jobId}/source-tree?refresh=true`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Refresh failed");
    } finally {
      setLoading(false);
    }
  };

  const onSort = (id) => {
    if (sortBy === id) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(id); setSortDir("asc"); }
  };

  const externalDlUrl = (path) => `${BACKEND_URL}/api/ingest/jobs/${jobId}/source-file?path=${encodeURIComponent(path)}`;

  const KIND_COLORS = {
    form_306: "bg-violet-100 text-violet-800 border-violet-200",
    ew_assessment_spreadsheet: "bg-violet-100 text-violet-800 border-violet-200",
    irw_assessment_spreadsheet: "bg-violet-100 text-violet-800 border-violet-200",
    epar_assessment_form: "bg-violet-100 text-violet-800 border-violet-200",
    smartygrants_application_pdf: "bg-violet-50 text-violet-700 border-violet-200",
    cost_estimation: "bg-blue-100 text-blue-800 border-blue-200",
    slip_construction_estimate: "bg-blue-50 text-blue-700 border-blue-200",
    financial_summary: "bg-emerald-100 text-emerald-800 border-emerald-200",
    evidence_photo: "bg-amber-50 text-amber-800 border-amber-200",
    image: "bg-amber-50 text-amber-700 border-amber-200",
    evidence_archive: "bg-orange-50 text-orange-700 border-orange-200",
    pdf_attachment: "bg-slate-100 text-slate-700 border-slate-200",
    site_risk_assessment: "bg-rose-50 text-rose-700 border-rose-200",
    certificate_of_currency: "bg-rose-50 text-rose-700 border-rose-200",
    spreadsheet: "bg-slate-100 text-slate-700 border-slate-200",
    rate_evidence: "bg-slate-100 text-slate-700 border-slate-200",
    email: "bg-slate-100 text-slate-700 border-slate-200",
    screening: "bg-slate-100 text-slate-700 border-slate-200",
    other: "bg-slate-100 text-slate-500 border-slate-200",
  };

  if (loading && !data) return (
    <div className="card p-5 text-sm text-slate-500" data-testid="source-tree-loading">Loading file tree…</div>
  );
  if (err) return <div className="card p-5 text-sm text-red-600">Source tree error: {err}</div>;
  if (!data) return null;

  const totalShown = filteredNodes.length;
  return (
    <div className="card p-5 mb-6" data-testid="source-tree">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold">Ingested package — file tree</h3>
            {data.disk_present ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] tracking-[0.14em] uppercase font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                Live · downloadable
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] tracking-[0.14em] uppercase font-bold bg-amber-100 text-amber-800 border border-amber-300">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"/>
                Snapshot · disk pruned
              </span>
            )}
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">
            {data.files_count} file{data.files_count === 1 ? "" : "s"} · {fmtBytes(data.total_bytes)} total · captured from <span className="font-mono">{data.root}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50" data-testid="source-tree-refresh">
            Refresh
          </button>
          <button
            onClick={() => setGroupByFolder(g => !g)}
            className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
            data-testid="source-tree-group-toggle"
          >
            {groupByFolder ? "Flat list" : "Group by folder"}
          </button>
        </div>
      </div>

      {/* Filter chips + search */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {[
          ["all", "All"],
          ["forms", "Claim forms"],
          ["cet", "CET workbooks"],
          ["slip", "Slip estimates"],
          ["ledger", "Ledgers"],
          ["photos", "Photos"],
          ["evidence_zips", "Evidence ZIPs"],
          ["pdfs", "PDFs"],
          ["spreadsheets", "Other Excel"],
          ["other", "Other"],
        ].map(([k, label]) => {
          // Compute count for the chip
          let count;
          const pred = CLASS_GROUPS[k];
          if (k === "all") count = data.files_count;
          else count = (data.nodes || []).filter(n => pred(n.classification)).length;
          if (k !== "all" && count === 0) return null;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`source-tree-filter-${k}`}
              className={`text-[11px] tracking-wider px-2.5 py-1 rounded-full border font-semibold ${
                filter === k
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {label} {filter === k ? "·" : ""} <span className={filter === k ? "text-slate-300 font-mono" : "text-slate-400 font-mono"}>{count}</span>
            </button>
          );
        })}
        <div className="flex-1 min-w-[160px]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or path…"
            className="w-full px-3 py-1.5 border border-slate-300 rounded text-[12px]"
            data-testid="source-tree-search"
          />
        </div>
      </div>

      {/* Header row */}
      <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-100 rounded text-[10px] tracking-[0.14em] uppercase font-bold text-slate-600">
        <div className="col-span-6"><SourceTreeSortableHeader id="path" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Path / name</SourceTreeSortableHeader></div>
        <div className="col-span-2"><SourceTreeSortableHeader id="classification" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Classification</SourceTreeSortableHeader></div>
        <div className="col-span-1"><SourceTreeSortableHeader id="ext" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Type</SourceTreeSortableHeader></div>
        <div className="col-span-1 text-right"><SourceTreeSortableHeader id="size" sortBy={sortBy} sortDir={sortDir} onSort={onSort}>Size</SourceTreeSortableHeader></div>
        <div className="col-span-2 text-right">Actions</div>
      </div>

      {totalShown === 0 ? (
        <div className="text-sm text-slate-500 italic py-6 text-center">No files match this filter / search.</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto" data-testid="source-tree-rows">
          {Object.entries(grouped).map(([folder, items]) => {
            const isOpen = groupByFolder ? (openFolders[folder] !== false) : true;
            return (
              <div key={folder}>
                {groupByFolder && (
                  <button
                    type="button"
                    onClick={() => setOpenFolders(s => ({ ...s, [folder]: !isOpen }))}
                    className="w-full grid grid-cols-12 gap-2 px-2 py-1 hover:bg-slate-50 text-left bg-slate-50/50"
                    data-testid={`source-tree-folder-${folder}`}
                  >
                    <div className="col-span-10 flex items-center gap-1.5 text-[11px] font-mono font-semibold text-slate-700">
                      <ChevronRight className={`w-3 h-3 transition-transform shrink-0 ${isOpen ? "rotate-90" : ""}`}/>
                      <span className="truncate" title={folder}>{folder || "(root)"}</span>
                    </div>
                    <div className="col-span-2 text-right text-[10px] text-slate-500">{items.length} file{items.length === 1 ? "" : "s"}</div>
                  </button>
                )}
                {isOpen && items.slice(0, showLimit).map((n) => (
                  <div key={n.path} className="grid grid-cols-12 gap-2 px-2 py-1.5 items-center text-[11.5px] hover:bg-slate-50">
                    <div className="col-span-6 min-w-0">
                      <div className="font-mono text-slate-800 truncate" title={n.path}>{n.name}</div>
                      {n.path !== n.name && (
                        <div className="text-[9.5px] text-slate-400 truncate font-mono" title={n.path}>{n.path}</div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <span className={`text-[9.5px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded border ${KIND_COLORS[n.classification] || KIND_COLORS.other}`}>
                        {n.classification.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="col-span-1 font-mono text-[10px] text-slate-500 uppercase">{n.ext || "—"}</div>
                    <div className="col-span-1 text-right font-mono text-[10px] text-slate-600">{n.size > 0 ? fmtBytes(n.size) : "—"}</div>
                    <div className="col-span-2 flex justify-end gap-1">
                      {n.exists && data.disk_present ? (
                        <a
                          href={externalDlUrl(n.path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[9.5px] tracking-wider uppercase font-semibold px-2 py-1 border border-slate-300 rounded hover:bg-slate-900 hover:text-white hover:border-slate-900"
                          data-testid={`source-tree-open-${n.name}`}
                          title="Open in new tab"
                        >
                          Open
                        </a>
                      ) : (
                        <span className="text-[9.5px] tracking-wider uppercase font-semibold text-slate-400">unavailable</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {totalShown > showLimit && (
            <div className="px-2 py-3 text-center">
              <button
                onClick={() => setShowLimit(l => l + 200)}
                className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
                data-testid="source-tree-show-more"
              >
                Show {Math.min(200, totalShown - showLimit)} more (of {totalShown - showLimit} remaining)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function VerifiedPackageTree({ jobId }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});  // {dm: bool, dm__folder: bool}
  const [inspect, setInspect] = useState(null);
  const [organising, setOrganising] = useState(false);
  const [organiseResult, setOrganiseResult] = useState(null);
  const [rebuild, setRebuild] = useState(null);      // { status, full_url, docs_url, full_size, docs_size, error }
  const [rebuilding, setRebuilding] = useState(false);
  const [copyFlash, setCopyFlash] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => { if (!cancelled) setLoading(true); });
    axios.get(`${API}/ingest/jobs/${jobId}/verified-package-tree`)
      .then(r => { if (!cancelled) { setTree(r.data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e?.response?.data?.detail || e?.message); setLoading(false); } });
    // Also seed the rebuild state from the last-cached job doc
    axios.get(`${API}/ingest/jobs/${jobId}`)
      .then(r => { if (!cancelled && r.data?.verified_package_rebuild) setRebuild(r.data.verified_package_rebuild); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jobId]);

  // Poll every 3s while a rebuild is running
  useEffect(() => {
    if (!rebuilding) return;
    let cancelled = false;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/ingest/jobs/${jobId}`);
        const rb = r.data?.verified_package_rebuild;
        if (rb && !cancelled) {
          setRebuild(rb);
          if (rb.status === "ok" || rb.status === "error") {
            setRebuilding(false);
          }
        }
      } catch { /* poll noop */ }
    }, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [rebuilding, jobId]);

  const rebuildPackage = async () => {
    setRebuilding(true);
    setRebuild({ status: "running", started_at: new Date().toISOString() });
    try {
      await axios.post(`${API}/ingest/jobs/${jobId}/rebuild-verified-package`);
    } catch (e) {
      setRebuild({ status: "error", error: e?.response?.data?.detail || e?.message });
      setRebuilding(false);
    }
  };

  const copyUrl = async (url, label) => {
    try {
      const full = `${BACKEND_URL}${url}`;
      await navigator.clipboard.writeText(full);
      setCopyFlash(label);
      setTimeout(() => setCopyFlash(""), 1500);
    } catch { /* clipboard blocked */ }
  };

  const organisePhotos = async () => {
    setOrganising(true);
    setOrganiseResult(null);
    try {
      const r = await axios.post(`${API}/ingest/jobs/${jobId}/auto-organise-photos`);
      setOrganiseResult(r.data);
      // Reload tree to reflect newly-organised folders
      const t = await axios.get(`${API}/ingest/jobs/${jobId}/verified-package-tree`);
      setTree(t.data);
    } catch (e) {
      setOrganiseResult({ error: e?.response?.data?.detail || e?.message });
    } finally {
      setOrganising(false);
    }
  };

  const downloadZip = () => {
    window.location.href = `${API}/ingest/jobs/${jobId}/verified-package`;
  };

  const toggle = (key) => setOpen(s => ({ ...s, [key]: !s[key] }));
  const expandAll = () => {
    if (!tree) return;
    const s = {};
    (tree.top_folders || []).forEach(f => { s[`top__${f.name}`] = true; });
    s["dm_section"] = true;
    (tree.dms || []).forEach(dm => { s[`dm__${dm.name}`] = true; (dm.folders || []).forEach(f => { s[`dm__${dm.name}__${f.name}`] = true; }); });
    setOpen(s);
  };
  const collapseAll = () => setOpen({});

  // Actions strip — always visible (regardless of tree loading state) so the
  // assessor can rebuild / auto-organise / download without waiting for the
  // preview tree to render.
  const actionsStrip = (
    <div className="mb-4 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3" data-testid="verified-package-actions">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={rebuildPackage}
          disabled={rebuilding}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[11.5px] font-semibold hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="rebuild-verified-package-btn"
        >
          {rebuilding
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Rebuilding…</>
            : <><RefreshCw className="w-3.5 h-3.5"/> Rebuild verified package</>
          }
        </button>
        <button
          type="button"
          onClick={organisePhotos}
          disabled={organising}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-[11.5px] font-semibold hover:bg-emerald-100 disabled:opacity-60"
          data-testid="auto-organise-photos-btn"
          title="Route damage / pre-event photos into the NSWRA folder buckets based on assessor DM decisions"
        >
          {organising
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Organising…</>
            : <><Wand2 className="w-3.5 h-3.5"/> Auto-organise photos</>
          }
        </button>
        <button
          type="button"
          onClick={downloadZip}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-slate-800 text-[11.5px] font-semibold hover:bg-slate-100"
          data-testid="download-verified-package-btn"
          title="Direct streaming download (may hit 60s ingress timeout on large packages — use Rebuild for those)"
        >
          <Download className="w-3.5 h-3.5"/> Direct download
        </button>
        <div className="ml-auto text-[10.5px] text-slate-500 leading-tight text-right">
          <div>Rebuild is async → beats the 60s ingress timeout.</div>
          <div>Writes both Full + DocsOnly to disk.</div>
        </div>
      </div>

      {rebuild && rebuild.status === "ok" && (
        <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 p-2.5" data-testid="rebuild-result-ok">
          <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-emerald-800 mb-1.5">
            <CheckCircle2 className="w-3.5 h-3.5"/> Rebuild complete — direct download links (no login required):
          </div>
          <div className="space-y-1 font-mono text-[11.5px]">
            <div className="flex items-center gap-1.5 flex-wrap">
              <a
                href={`${BACKEND_URL}${rebuild.full_url}`}
                className="text-emerald-800 underline hover:text-emerald-950 truncate max-w-[520px]"
                data-testid="rebuild-full-link"
              >{rebuild.full_url}</a>
              <span className="text-[10.5px] text-slate-500 shrink-0">{fmtBytes(rebuild.full_size)} · full (with photos)</span>
              <button
                type="button"
                onClick={() => copyUrl(rebuild.full_url, "full")}
                className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 hover:bg-white text-[10.5px]"
                data-testid="rebuild-copy-full"
              >
                {copyFlash === "full" ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
              </button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <a
                href={`${BACKEND_URL}${rebuild.docs_url}`}
                className="text-emerald-800 underline hover:text-emerald-950 truncate max-w-[520px]"
                data-testid="rebuild-docs-link"
              >{rebuild.docs_url}</a>
              <span className="text-[10.5px] text-slate-500 shrink-0">{fmtBytes(rebuild.docs_size)} · docs only (no photos)</span>
              <button
                type="button"
                onClick={() => copyUrl(rebuild.docs_url, "docs")}
                className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 hover:bg-white text-[10.5px]"
                data-testid="rebuild-copy-docs"
              >
                {copyFlash === "docs" ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
              </button>
            </div>
          </div>
          <div className="mt-1.5 text-[10.5px] text-slate-500">
            Structure: <span className="font-mono text-slate-700">{rebuild.sg_id}/&lt;Assessment Form &vert; Verified CET Evidence &vert; Damage Evidence…&gt;/file</span> · 3 levels max.
          </div>
        </div>
      )}
      {rebuild && rebuild.status === "running" && (
        <div className="mt-3 rounded-md bg-slate-100 border border-slate-200 p-2.5 text-[11.5px] text-slate-700 flex items-center gap-2" data-testid="rebuild-result-running">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/>
          <span>Rebuilding in background — this usually takes 20–90 seconds depending on photo count. Safe to navigate away and come back.</span>
        </div>
      )}
      {rebuild && rebuild.status === "error" && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-2.5 text-[11.5px] text-red-800" data-testid="rebuild-result-error">
          <div className="font-semibold mb-0.5">Rebuild failed</div>
          <div className="font-mono">{rebuild.error}</div>
        </div>
      )}
      {organiseResult && !organiseResult.error && (
        <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 p-2 text-[11.5px] text-emerald-800" data-testid="organise-result-ok">
          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1"/>
          Auto-organise complete · {organiseResult.moved_files || 0} photos moved · {organiseResult.dms_processed || 0} DMs processed.
        </div>
      )}
      {organiseResult?.error && (
        <div className="mt-2 rounded-md bg-red-50 border border-red-200 p-2 text-[11.5px] text-red-800" data-testid="organise-result-error">
          Auto-organise failed: {organiseResult.error}
        </div>
      )}
    </div>
  );

  if (loading) return (
    <div className="card p-5 mb-6" data-testid="verified-package-tree-loading">
      {actionsStrip}
      <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Building verified package tree…</div>
    </div>
  );
  // 410 / network error — keep red banner for genuine failures, but make it
  // less alarming (slate not red, badge not banner).
  if (err) return (
    <div className="card p-5 mb-6 text-sm text-slate-600 border-slate-200" data-testid="verified-package-tree-error">
      {actionsStrip}
      <div className="font-semibold text-slate-700 mb-1">Couldn&apos;t load the verified package tree</div>
      <div className="text-[12.5px]">{err}</div>
    </div>
  );
  if (!tree) return null;
  // Empty state — backend returned `empty:true` because there's no work_dir
  // on disk AND no Mongo snapshot. Render a calm, actionable recovery panel
  // (NOT a red error banner) so the rest of the page remains usable.
  if (tree.empty) return (
    <div className="card p-6 mb-6 border-amber-200 bg-amber-50/40" data-testid="verified-package-tree-empty">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-amber-700"/>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-semibold text-slate-900 mb-0.5">Verified package preview not available yet</h3>
          <p className="text-[12.5px] text-slate-700 leading-relaxed">
            {tree.message || "The ingest directory is no longer on disk and no cached snapshot was found for this job."}
          </p>
          <p className="text-[11.5px] text-slate-500 mt-1">
            This usually happens for demo/seed jobs or jobs ingested before snapshot caching shipped. The rest of the assessor workflow (header, photos, ledger, decisions) still works — only the file-level preview is unavailable.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/inbox" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[11.5px] font-semibold hover:bg-slate-700" data-testid="verified-tree-reingest-btn">
              <Upload className="w-3.5 h-3.5"/> Re-ingest the package
            </Link>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-[11.5px] font-semibold hover:bg-white"
              data-testid="verified-tree-retry-btn"
            >Retry</button>
          </div>
        </div>
      </div>
    </div>
  );
  // Defensive: backend may return a partial payload (e.g. legacy snapshot
  // without `dms`/`summary`). Backfill so the render below can't crash.
  const summary = tree.summary || { dm_count: 0, verified_dms: 0, non_verified_dms: 0, total_files: 0, total_bytes: 0 };
  const dms = Array.isArray(tree.dms) ? tree.dms : [];

  const renderFile = (f, i) => (
    <button
      key={`${f.path || f.name}-${i}`}
      type="button"
      onClick={() => f.path && setInspect(f)}
      disabled={!f.path}
      className={`flex items-center gap-1.5 text-left w-full px-1 py-0.5 rounded group ${
        f.path ? "hover:bg-white hover:text-slate-900 cursor-pointer" : "italic text-slate-400 cursor-not-allowed"
      }`}
      data-testid={`tree-file-${f.name}`}
      title={f.path ? "Click to inspect file" : f.note || "Placeholder — generated on download"}
    >
      {f.kind === "image" ? (
        <ImageIcon className="w-3 h-3 text-slate-400 group-hover:text-slate-700"/>
      ) : f.kind === "workbook" ? (
        <FileCheck2 className="w-3 h-3 text-emerald-600"/>
      ) : f.kind === "pdf" ? (
        <ScrollText className="w-3 h-3 text-red-500"/>
      ) : (
        <FileCheck2 className="w-3 h-3 text-slate-400"/>
      )}
      <span className={`truncate ${f.path ? "text-slate-700 group-hover:underline" : ""}`}>{f.name}</span>
      {f.note && <span className="text-[10.5px] text-slate-400 italic">— {f.note}</span>}
      {f.path && <span className="text-[10.5px] text-slate-400 ml-auto shrink-0">{fmtBytes(f.size)}</span>}
    </button>
  );

  const renderTopFolder = (folder) => {
    const key = `top__${folder.name}`;
    const isPlaceholder = folder.status === "placeholder";
    const isEmpty = folder.status === "empty";
    return (
      <div key={folder.name} className="ml-4 mt-1.5">
        <button
          type="button"
          onClick={() => toggle(key)}
          className="flex items-center gap-1.5 text-left hover:text-slate-900 w-full"
          data-testid={`tree-toggle-top-${folder.name.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <ChevronRight className={`w-3 h-3 transition-transform ${open[key] ? "rotate-90" : ""}`}/>
          <FolderKanban className={`w-3.5 h-3.5 ${
            isPlaceholder ? "text-amber-500"
              : isEmpty ? "text-slate-400"
              : folder.name.toLowerCase().includes("non verified") ? "text-amber-600"
              : folder.name.toLowerCase().includes("verified") ? "text-emerald-600"
              : "text-slate-600"
          }`}/>
          <span className="font-semibold text-slate-700">{folder.name}</span>
          <span className="text-[10.5px] text-slate-500">
            {folder.count} {folder.count === 1 ? "file" : "files"}
            {folder.bytes > 0 ? ` · ${fmtBytes(folder.bytes)}` : ""}
          </span>
          {isPlaceholder && (
            <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-700 px-1 bg-amber-100 rounded">placeholder</span>
          )}
          {isEmpty && (
            <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500 px-1 bg-slate-200 rounded">empty</span>
          )}
        </button>
        {open[key] && (
          <div className="ml-6 mt-1 space-y-0.5">
            {folder.files.length === 0
              ? <div className="italic text-slate-400 text-[11.5px]">(no files)</div>
              : folder.files.map(renderFile)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card p-5 mb-6" data-testid="verified-package-tree">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold">Verified assessment package · folder tree</h3>
          <p className="text-[11.5px] text-slate-500 mt-1">Mirrors the NSWRA File Structure spec. <span className="font-semibold text-slate-700">Non Verified</span> = data the assessor could not substantiate (RFI may be required). <span className="font-semibold text-slate-700">Verified</span> = substantiated by the assessor (per-DM YES). Click any file to inspect its quality.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={collapseAll} className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-900">Collapse</button>
          <span className="text-slate-300">·</span>
          <button onClick={expandAll} className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-900">Expand all</button>
        </div>
      </div>

      {/* Action strip lifted above to render regardless of loading/error state */}
      {actionsStrip}


      <div className="font-mono text-[12px] bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto">
        {/* Root path: Documents / Cat B / <SG_ID> / Application Assessment */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <FolderKanban className="w-3.5 h-3.5 text-slate-600 shrink-0"/>
          <span className="font-bold text-slate-800">Documents</span>
          <span className="text-slate-400">/</span>
          <span className="font-bold text-slate-800">Cat B</span>
          <span className="text-slate-400">/</span>
          <span className="font-bold text-slate-800" title="SmartyGrants ID — rename to actual SG ID">{tree.root.sg_id}</span>
          <span className="text-slate-400">/</span>
          <span className="font-bold text-slate-800">Application Assessment</span>
          <span className="ml-auto text-[10.5px] text-slate-500">
            {summary.dm_count} DMs ({summary.verified_dms} verified · {summary.non_verified_dms} non-verified) · {summary.total_files} files · {fmtBytes(summary.total_bytes)}
          </span>
        </div>

        {/* Top-level Application Assessment folders */}
        {(tree.top_folders || []).map(renderTopFolder)}

        {/* Verified Damage Evidence (container) + per-DM folders */}
        <div className="ml-4 mt-1.5">
          <button
            type="button"
            onClick={() => toggle("dm_section")}
            className="flex items-center gap-1.5 text-left hover:text-slate-900 w-full"
            data-testid="tree-toggle-damage-section"
          >
            <ChevronRight className={`w-3 h-3 transition-transform ${open["dm_section"] ? "rotate-90" : ""}`}/>
            <FolderKanban className="w-3.5 h-3.5 text-slate-600"/>
            <span className="font-semibold text-slate-700">Verified Damage Evidence</span>
            <span className="text-[10.5px] text-slate-500">{dms.length} {dms.length === 1 ? "damage item" : "damage items"}</span>
          </button>
          {open["dm_section"] && (
            <div className="ml-6 mt-1">
              {dms.map((dm) => {
                const dkey = `dm__${dm.name}`;
                return (
                  <div key={dm.name} className="mt-1.5">
                    <button
                      type="button"
                      onClick={() => toggle(dkey)}
                      className="flex items-center gap-1.5 text-left hover:text-slate-900 w-full"
                      data-testid={`tree-toggle-dm-${dm.name}`}
                    >
                      <ChevronRight className={`w-3 h-3 transition-transform ${open[dkey] ? "rotate-90" : ""}`}/>
                      <FolderKanban className={`w-3.5 h-3.5 ${dm.verified ? "text-emerald-600" : dm.decision?.verified ? "text-amber-600" : "text-slate-500"}`}/>
                      <span className="font-semibold text-slate-800">{dm.name}</span>
                      <span className="text-[10.5px] text-slate-500">{dm.count} files · {fmtBytes(dm.bytes)}</span>
                      <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                        dm.verified ? "bg-emerald-100 text-emerald-700"
                        : dm.decision?.verified === "RFI" ? "bg-amber-100 text-amber-700"
                        : dm.decision?.verified === "No" ? "bg-red-100 text-red-700"
                        : "bg-slate-200 text-slate-600"
                      }`}>
                        {dm.decision?.verified || "not decided"}
                      </span>
                    </button>
                    {open[dkey] && (
                      <div className="ml-6 mt-1">
                        {dm.folders.map((sub) => {
                          const skey = `dm__${dm.name}__${sub.name}`;
                          return (
                            <div key={sub.name} className="mt-1">
                              <button
                                type="button"
                                onClick={() => toggle(skey)}
                                className="flex items-center gap-1.5 text-left hover:text-slate-900 w-full"
                                data-testid={`tree-toggle-${dm.name}-${sub.name.split(" ")[0].replace(".", "")}`}
                              >
                                <ChevronRight className={`w-3 h-3 transition-transform ${open[skey] ? "rotate-90" : ""}`}/>
                                <FolderKanban className={`w-3.5 h-3.5 ${
                                  sub.name.toLowerCase().includes("non verified") ? "text-amber-600"
                                    : sub.count > 0 ? "text-emerald-600"
                                    : "text-slate-400"
                                }`}/>
                                <span className="text-slate-700">{sub.name}</span>
                                <span className="text-[10.5px] text-slate-500">{sub.count} {sub.count === 1 ? "file" : "files"}</span>
                                {sub.count === 0 && (
                                  <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500 px-1 bg-slate-200 rounded">empty</span>
                                )}
                              </button>
                              {open[skey] && (
                                <div className="ml-6 mt-1 space-y-0.5">
                                  {sub.files.length === 0
                                    ? <div className="italic text-slate-400 text-[11.5px]">(no files)</div>
                                    : sub.files.map(renderFile)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <FileInspectModal jobId={jobId} file={inspect} onClose={() => setInspect(null)} />
    </div>
  );
}

